import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { patchQmr, useQmrDispatch, useQmrState } from '@packages/contexts/qmrs';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { Typography, Button } from '@packages/ui/shared';
import { qmrsService, analyticsService } from '@web/services/singletons';
import { Modal } from 'react-bootstrap';
import { SelectQmrPrimaryObjectives } from '@web/components/select-qmr-primary-objectives';
import { ANALYTICS_EVENTS } from '@packages/core/analytics/amplitude/amplitude-event-types';

const logger = getLogger('qmr');

const useStyles = createUseStyles({
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    primaryOutlineBtn: {
        color: colors.blueOne,
        borderColor: colors.blueOne,
        padding: 10,
        borderRadius: 4,
        '&:hover, &:active': {
            backgroundColor: 'transparent',
            borderColor: colors.blueOne,
        },
    },
    headerText: {
        fontSize: '21px',
    },
});

const PrimaryObjective = ({ viewOnly = false }: any) => {
    const classes = useStyles();
    const { abortSignalRef } = useAbortController();
    const { qmr } = useQmrState();
    const qmrDispatch = useQmrDispatch();

    const [qmrCategories, setQmrCategories] = useState([]);
    const [showObjectiveModal, setShowObjectiveModal] = useState(false);
    const [selectedPrimaryObjectives, setSelectedPrimaryObjectives] = useState([]);
    const [showObjectiveConfirmationModal, setShowObjectiveConfirmationModal] = useState(false);
    const [showNQRQMRConfirmationModal, setShowNQRQMRConfirmationModal] = useState(false);
    const [displayModalBody, setDisplayModalBody] = useState(true);

    const fetchQmrCategories = async () => {
        try {
            const qmrCategories: any = await qmrsService.fetchQmrCategories();
            if (qmrCategories?.data?.QmrCategories?.length) {
                setQmrCategories(
                    qmrCategories.data.QmrCategories.map((category: any) => ({ ...category, isSelected: false }))
                );
            }
        } catch (err) {
            logger.error('Failed to fetch QMR categories', { error: err });
        }
    };

    useEffect(() => {
        fetchQmrCategories();
    }, []);

    const selectedQmrCategories = useMemo(() => {
        return qmrCategories.filter((c: any) => {
            return qmr?.qmrCategories && qmr.qmrCategories.includes(c.qmrCategoryId);
        });
    }, [qmr?.qmrCategories, qmrCategories]);

    const renderQmrCategories = () => {
        if (!selectedQmrCategories?.length) return null;
        return selectedQmrCategories.map((category: any) => {
            return (
                <div>
                    <Typography variant="caption3">{category.description}</Typography>
                </div>
            );
        });
    };

    const handleObjectiveUpdate = useCallback(
        async (extraParam?: any, qmrCategoryId?: string[]) => {
            if (!qmr) {
                return;
            }

            await patchQmr({
                qmrId: qmr.qmrId,
                qmrsService,
                qmrDispatch,
                isLocalPatch: false,
                signal: abortSignalRef.current,
                qmrPatch: {
                    qmrCategories: qmrCategoryId
                        ? qmrCategoryId
                        : selectedPrimaryObjectives.map((c: any) => c.qmrCategoryId),
                    ...extraParam,
                },
            });

            qmr?.isNqr
                ? analyticsService.logEvent(ANALYTICS_EVENTS.NQR_CONVERTED_TO_QMR)
                : analyticsService.logEvent(ANALYTICS_EVENTS.QMR_CONVERTED_TO_NQR);
            setShowObjectiveConfirmationModal(false);
            setShowObjectiveModal(false);
            setShowNQRQMRConfirmationModal(false);
        },
        [abortSignalRef, qmr, qmrDispatch, selectedPrimaryObjectives]
    );

    const onPrimaryObjectivesSelected = (selectedOptions: any) => {
        setSelectedPrimaryObjectives(selectedOptions);
    };
    const displayModalandBody = () => {
        setShowObjectiveModal(true);
        setDisplayModalBody(true);
    };
    const confirmAndUpdateObjective = () => {
        const isPrimaryObjectiveNQRorQMR =
            selectedPrimaryObjectives.length && selectedPrimaryObjectives.some((c: any) => c.qmrCategoryType === 'QMR')
                ? 'QMR'
                : 'NQR';

        if (isPrimaryObjectiveNQRorQMR === 'NQR') {
            if (qmr?.ewdSignsExist) {
                setShowObjectiveModal(false);
                setShowNQRQMRConfirmationModal(true);
            } else if (!qmr?.isNqr) {
                setShowObjectiveModal(false);
                setShowObjectiveConfirmationModal(true);
            } else {
                handleObjectiveUpdate();
            }
        } else if (qmr?.isNqr) {
            setShowObjectiveModal(false);
            setShowObjectiveConfirmationModal(true);
        } else {
            handleObjectiveUpdate();
        }
    };

    const renderSelectObjectiveModal = () => {
        return (
            <Modal show={showObjectiveModal} onHide={() => setShowObjectiveModal(false)} size="lg" centered>
                <Modal.Header>
                    <b>Update Primary Objective</b>
                </Modal.Header>
                {displayModalBody && (
                    <Modal.Body>
                        <SelectQmrPrimaryObjectives
                            defaultSelected={selectedQmrCategories}
                            onSelect={onPrimaryObjectivesSelected}
                            displayCategoryType
                        />
                    </Modal.Body>
                )}
                <Modal.Footer>
                    <div className="mr-2">
                        <Button variant="ghost-blue" onPress={() => setShowObjectiveModal(false)}>
                            Cancel
                        </Button>
                    </div>
                    <Button disabled={!selectedPrimaryObjectives?.length} onPress={confirmAndUpdateObjective}>
                        Update
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const renderNQRQMRConfirmationModal = () => {
        return (
            <Modal
                show={showNQRQMRConfirmationModal}
                onHide={() => setShowNQRQMRConfirmationModal(false)}
                size="lg"
                centered
            >
                <Modal.Header>
                    <b>About to change the report type</b>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        <Typography>
                            Your original selection of <b>Yes</b> for the question:
                            <b>
                                {' '}
                                Did the customer report any of the following: Rollover, Fire, Accident, Injury, Death,
                                Property Damage?{' '}
                            </b>
                            warrants this report to be submitted as a QMR. Now that you have changed the primary
                            objective to mark the record as an NQR the answer will be changed to
                            <b> No. </b>
                            <div style={{ paddingTop: 15 }}>
                                <Typography>How do you want to proceed?</Typography>
                            </div>
                        </Typography>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ justifyContent: 'space-between' }}>
                    <div>
                        <Button variant="ghost-blue" onPress={() => setShowNQRQMRConfirmationModal(false)}>
                            Cancel
                        </Button>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            variant="ghost-blue"
                            onPress={() => handleObjectiveUpdate({ ewdSignsExist: true }, ['REPAIR_CONCERN'])}
                        >
                            Proceed with QMR
                        </Button>
                        <Button onPress={() => handleObjectiveUpdate({ ewdSignsExist: false })}>
                            Proceed with NQR
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    };

    const renderPrimaryObjectiveConfirmationModal = () => {
        return (
            <Modal
                show={showObjectiveConfirmationModal}
                onHide={() => setShowObjectiveConfirmationModal(false)}
                size="lg"
                centered
            >
                <Modal.Header>
                    <b>Confirm update and change report type</b>
                </Modal.Header>
                <Modal.Body>
                    <Typography>
                        By selecting this objective(s), you are about to change the{' '}
                        <b>{qmr?.isNqr ? 'NQR to QMR' : 'QMR to NQR'}</b>.
                    </Typography>
                    <div>
                        <Typography>Are you sure you want to proceed?</Typography>
                    </div>
                    <div className="mt-5">
                        <Typography>
                            *Note: Changing the report type will move it to corresponding flow and section.
                        </Typography>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ justifyContent: 'space-between' }}>
                    <div>
                        <Button
                            variant="ghost-blue"
                            onPress={() => {
                                setShowObjectiveConfirmationModal(false);
                                displayModalandBody();
                            }}
                        >
                            Back
                        </Button>
                    </div>
                    <div style={{ display: 'flex' }}>
                        <div className="mr-2">
                            <Button
                                variant="ghost-blue"
                                onPress={() => {
                                    (setShowObjectiveConfirmationModal(false), setDisplayModalBody(true));
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                        <Button onPress={() => handleObjectiveUpdate(!qmr?.isNqr ? { ewdSignsExist: false } : {})}>
                            Confirm
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    };

    return (
        <div>
            <div className={classes.header}>
                <Typography style={{ fontSize: 21 }} variant="caption3">
                    Primary objective
                </Typography>
                {!viewOnly ? (
                    <button
                        onClick={() => {
                            displayModalandBody();
                        }}
                        type="button"
                        className={`${classes.primaryOutlineBtn} btn btn-outline-primary`}
                    >
                        <Typography variant="h6" color="blueOne">
                            Change Primary Objective
                        </Typography>
                    </button>
                ) : (
                    <></>
                )}
            </div>
            <div className="mt-3">{renderQmrCategories()}</div>
            {renderSelectObjectiveModal()}
            {renderPrimaryObjectiveConfirmationModal()}
            {renderNQRQMRConfirmationModal()}
        </div>
    );
};

export default PrimaryObjective;
